import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  InputAdornment,
  Typography,
  Button,
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useSnackbar } from "../../App";
import { getAllBranchLists } from "../../service/branchService";
import { getAllRoles } from "../../service/roleService";
import Color from "../../components/Color";


const defaultFormData = {
  name: "",
  email: "",
  password: "",
  password_confirmation: "",
  branch_id: "",
  role_id: "",
  status: true,
};


const passwordRequirements = [
  { label: "Minimum 8 characters", regex: /^.{8,}$/ },
  { label: "At least 1 uppercase letter", regex: /[A-Z]/ },
  { label: "At least 1 number", regex: /[0-9]/ },
  {
    label: "At least 1 special character",
    regex: /[!@#$%^&*()_\-+=~`{}\[\]:;"'<>,.?/\\|]/,
  },
];

const isPasswordValid = (password) =>
  passwordRequirements.every((req) => req.regex.test(password));

// Password strength and hint calculator
const getPasswordStrengthAndHints = (password) => {
  if (!password) {
    return { label: "Low", color: "red", hints: passwordRequirements.map((req) => req.label) };
  }
  const metRequirements = passwordRequirements.filter((req) => req.regex.test(password)).length;
  const hints = passwordRequirements
    .filter((req) => !req.regex.test(password))
    .map((req) => req.label);

  if (metRequirements <= 1) {
    return { label: "Low", color: "red", hints };
  } else if (metRequirements <= 3) {
    return { label: "Medium", color: "orange", hints };
  } else {
    return { label: "Strong", color: "green", hints: [] };
  }
};

const AddUser = ({ open, onClose, formData, setFormData, onSubmit }) => {
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordStrengthData, setPasswordStrengthData] = useState(
    getPasswordStrengthAndHints(formData.password)
  );
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    if (open) {
      setPasswordStrengthData(getPasswordStrengthAndHints(formData.password));
    }
  }, [open, formData.password]);

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        try {
          const roleRes = await getAllRoles();
          setRoles(Array.isArray(roleRes?.data?.data) ? roleRes.data.data : []);
        } catch {
          showSnackbar("Failed to load roles", "error");
        }

        try {
          const res = await getAllBranchLists();
          const list = res?.data?.data || [];
          setBranches(Array.isArray(list) ? list : []);
        } catch {
          showSnackbar("Failed to fetch branches!", "error");
        }
      };
      fetchData();
    }
  }, [open, showSnackbar]);

  const handleChange = (e) => {
    let { name, value, type, checked } = e.target;

    if (name === "email") {
      value = value.toLowerCase();
      value = value.replace(/[^a-z0-9@.]/g, "");
    }

    const newValue = type === "checkbox" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));

    if (name === "password") {
      setPasswordStrengthData(getPasswordStrengthAndHints(value));
    }

    switch (name) {
      case "name":
        if (!value.trim()) {
          setFormErrors((prev) => ({ ...prev, name: "Name is required" }));
        } else if (/[^a-zA-Z ]/.test(value)) {
          setFormErrors((prev) => ({
            ...prev,
            name: "Name must contain only letters",
          }));
        } else {
          setFormErrors((prev) => ({ ...prev, name: "" }));
        }
        break;

      case "email":
        if (!value.trim()) {
          setFormErrors((prev) => ({ ...prev, email: "Email is required" }));
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          setFormErrors((prev) => ({ ...prev, email: "Invalid email format" }));
        } else {
          setFormErrors((prev) => ({ ...prev, email: "" }));
        }
        break;

      case "password_confirmation":
        if (value !== formData.password) {
          setFormErrors((prev) => ({
            ...prev,
            password_confirmation: "Passwords do not match",
          }));
        } else {
          setFormErrors((prev) => ({ ...prev, password_confirmation: "" }));
        }
        break;

      case "branch_id":
        setFormErrors((prev) => ({
          ...prev,
          branch_id: value ? "" : "Please select a branch",
        }));
        break;

      case "role_id":
        setFormErrors((prev) => ({
          ...prev,
          role_id: value ? "" : "Please select a role",
        }));
        break;

      default:
        break;
    }
  };

  const validate = () => {
    const errors = {};
    const { name, email, password, password_confirmation, branch_id, role_id, id } = formData;

    if (!name.trim()) {
      errors.name = "Name is required";
    } else if (!/^[a-zA-Z ]+$/.test(name)) {
      errors.name = "Name must contain only letters";
    }

    if (!email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = "Invalid email format";
    }

    if (!id) {
      if (!isPasswordValid(password)) {
        errors.password = "Password does not meet requirements";
      }
      if (password !== password_confirmation) {
        errors.password_confirmation = "Passwords do not match";
      }
    }

    if (!role_id) errors.role_id = "Please select a role";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleClose = () => {
    setFormErrors({});
    setFormData(defaultFormData);
    setShowPassword(false);
    setShowConfirm(false);
    setPasswordStrengthData(getPasswordStrengthAndHints("")); // Reset strength
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true);
      try {
        await onSubmit();
        handleClose();
      } catch (err) {
        const serverErrors = err?.response?.data?.errors || {};
        setFormErrors((prev) => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(serverErrors).map(([key, val]) => [key, val[0]])
          ),
        }));
        showSnackbar(err?.response?.data?.message || "Failed to save user.", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  // Use password strength from state
  const { label: passwordStrength, color: strengthColor, hints } = passwordStrengthData;

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleClose();
        }
      }}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          width: "1100px",
          maxWidth: "95vw",
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle>{formData?.id ? "Edit User" : "Add User"}</DialogTitle>
      <DialogContent
        dividers
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1, sm: 2 },
          maxHeight: { xs: "60vh", sm: "auto" },
          overflowY: "auto",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            {/* Row 1: Name and Email */}
            <Box>
              <Typography
                variant="body1"
                sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
              >
                Name*
              </Typography>
              <TextField
                name="name"
                fullWidth
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter name"
                error={!!formErrors.name}
                helperText={formErrors.name}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{ "& .MuiInputLabel-root": { display: "none" }, mb: 1 }}
              />
            </Box>
            <Box>
              <Typography
                variant="body1"
                sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
              >
                Email*
              </Typography>
              <TextField
                name="email"
                fullWidth
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                error={!!formErrors.email}
                helperText={formErrors.email}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{ "& .MuiInputLabel-root": { display: "none" }, mb: 1 }}
              />
            </Box>

            {/* Row 2: Password and Confirm Password */}
            {!formData.id ? (
              <>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
                  >
                    Password*
                  </Typography>
                  <TextField
                    name="password"
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter password"
                    error={!!formErrors.password && !isPasswordValid(formData.password)}
                    helperText={
                      <Stack spacing={0.6}>
                        {/* Password Strength Indicator */}
                        <Typography
                          variant="caption"
                          sx={{
                            mt: 1,
                            fontWeight: "bold",
                            color: strengthColor,
                          }}
                        >
                          Password Strength: {passwordStrength}
                        </Typography>

                        {/*  password requirements */}
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", fontSize: "10.3px" }}
                        >
                          {passwordRequirements.map((req, index) => {
                            const met = req.regex.test(formData.password);
                            return (
                              <span key={index}>
                                {req.label}
                                {index < passwordRequirements.length - 1 ? ", " : ""}
                              </span>
                            );
                          })}
                        </Typography>

                      </Stack>
                    }
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    sx={{ "& .MuiInputLabel-root": { display: "none" }, mb: 1 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword((prev) => !prev)}>
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
                  >
                    Confirm Password*
                  </Typography>
                  <TextField
                    name="password_confirmation"
                    type={showConfirm ? "text" : "password"}
                    fullWidth
                    value={formData.password_confirmation}
                    onChange={handleChange}
                    placeholder="Confirm password"
                    error={!!formErrors.password_confirmation}
                    helperText={formErrors.password_confirmation}
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    sx={{ "& .MuiInputLabel-root": { display: "none" }, mb: 1 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowConfirm((prev) => !prev)}>
                            {showConfirm ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </>
            ) : (
              <>
              </>
            )}

            {/* Row 3: Role and Branch */}
            <Box>
              <Typography
                variant="body1"
                sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
              >
                Role*
              </Typography>
              <FormControl fullWidth size="small" error={!!formErrors.role_id} sx={{ mb: 1 }}>
                <Select
                  name="role_id"
                  value={formData.role_id ?? ""}
                  onChange={handleChange}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) {
                      return (
                        <Box sx={{ color: "text.secondary" }}>
                          Select role
                        </Box>
                      );
                    }
                    return roles.find((role) => role.id === value)?.name || "";
                  }}
                >
                  <MenuItem value="">
                    Select role
                  </MenuItem>
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.role_id && <FormHelperText>{formErrors.role_id}</FormHelperText>}
              </FormControl>
            </Box>

            {(() => {
              const selectedRole = roles.find((r) => r.id === formData.role_id);
              if (selectedRole?.name?.toLowerCase() !== "admin") {
                return (
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ mb: 1, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
                    >
                      Branch
                    </Typography>
                    <FormControl fullWidth size="small" error={!!formErrors.branch_id} sx={{ mb: 1 }}>
                      <Select
                        name="branch_id"
                        value={formData.branch_id ?? ""}
                        onChange={handleChange}
                        displayEmpty
                        renderValue={(value) => {
                          if (!value) {
                            return (
                              <Box sx={{ color: "text.secondary" }}>
                                Select branch
                              </Box>
                            );
                          }
                          return branches.find((branch) => branch.id === value)?.name || "";
                        }}
                      >
                        <MenuItem value="">
                          Select branch
                        </MenuItem>
                        {branches.map((branch) => (
                          <MenuItem key={branch.id} value={branch.id}>
                            {branch.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors.branch_id && <FormHelperText>{formErrors.branch_id}</FormHelperText>}
                    </FormControl>
                  </Box>
                );
              }
              return <Box></Box>;
            })()}
          </Box>

          {/* Status Switch */}
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="body1"
              sx={{ mb: 0, fontWeight: "bold", color: Color.label.main, fontSize: "13px" }}
            >
              Status
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.status}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: e.target.checked,
                    }))
                  }
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: Color.primary.main,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: Color.primary.main,
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: "#ccc",
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    color: formData.status ? Color.primary.main : "text.secondary",
                    fontWeight: "bold",
                  }}
                >
                  {formData.status ? "Active" : "Inactive"}
                </Typography>
              }
            />
          </Box>

          {/* Submit and Cancel Buttons */}
          <Box display="flex" justifyContent="flex-end" gap={2} mt={1} mb={1}>
            <Button
              onClick={handleClose}
              sx={{
                background: "none",
                color: Color.secondary.main,
                border: "1px solid",
                borderColor: Color.secondary.main,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              sx={{ background: Color.primary.main, color: "white" }}
              disabled={loading}
            >
              {loading ? "Saving..." : formData?.id ? "Update" : "Create"}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddUser;